import React, { useState, useEffect } from 'react';
import clusterManager from '../Map/VehicleClusterManager';

/**
 * Map Performance Monitor
 * Tracks and displays map rendering performance metrics
 * Helps optimize marker clustering and rendering performance
 */
const MapPerformanceMonitor = ({ 
  totalVehicles = 0, 
  visibleVehicles = 0,
  enableClustering = true 
}) => {
  const [metrics, setMetrics] = useState({});
  const [testResults, setTestResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  // Update metrics every 2 seconds
  useEffect(() => {
    const updateMetrics = () => {
      const clusterMetrics = clusterManager.getMetrics();
      setMetrics({
        ...clusterMetrics,
        totalVehicles,
        visibleVehicles,
        clusteringEnabled: enableClustering
      });
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 2000);
    
    return () => clearInterval(interval);
  }, [totalVehicles, visibleVehicles, enableClustering]);

  // Generate test vehicles for performance testing
  const generateTestVehicles = (count) => {
    const vehicles = [];
    const belizeCenter = { lat: 17.2515, lng: -88.7611 };
    
    for (let i = 0; i < count; i++) {
      vehicles.push({
        id: `test_vehicle_${i}`,
        vehicle_type: i % 4 === 0 ? 'bus' : 'car',
        make: 'Test',
        model: 'Vehicle',
        year: 2020 + (i % 5),
        license_plate: `TEST${i.toString().padStart(3, '0')}`,
        color: ['Red', 'Blue', 'Green', 'Yellow', 'Black'][i % 5],
        last_location: {
          latitude: belizeCenter.lat + (Math.random() - 0.5) * 0.5,
          longitude: belizeCenter.lng + (Math.random() - 0.5) * 0.5,
          speed: Math.random() * 60
        },
        last_updated: new Date().toISOString()
      });
    }
    
    return vehicles;
  };

  // Run performance test with different vehicle counts
  const runPerformanceTest = async () => {
    setIsRunning(true);

    try {
      const testScenarios = [
        { count: 50, name: 'Small Load' },
        { count: 100, name: 'Medium Load' },
        { count: 200, name: 'High Load' },
        { count: 500, name: 'Extreme Load' },
        { count: 1000, name: 'Stress Test' }
      ];

      const results = [];
      
      for (const scenario of testScenarios) {
        
        const testVehicles = generateTestVehicles(scenario.count);
        const mockBounds = {
          north: 17.5,
          south: 17.0,
          east: -88.5,
          west: -89.0
        };

        // Test with clustering
        clusterManager.clearCache();
        const clusterStart = performance.now();
        const clusterResult = clusterManager.clusterVehicles(testVehicles, 13, mockBounds);
        const clusterTime = performance.now() - clusterStart;

        // Test without clustering (simulate individual markers)
        const individualStart = performance.now();
        const visibleIndividual = testVehicles.filter(vehicle => 
          clusterManager.isInViewport(
            vehicle.last_location.latitude,
            vehicle.last_location.longitude,
            mockBounds
          )
        );
        const individualTime = performance.now() - individualStart;

        results.push({
          ...scenario,
          clusterTime: clusterTime.toFixed(1),
          individualTime: individualTime.toFixed(1),
          clustersCreated: clusterResult.clusters.length,
          visibleIndividual: visibleIndividual.length,
          improvement: ((individualTime - clusterTime) / individualTime * 100).toFixed(1),
          memoryUsage: clusterManager.iconCache.size
        });

        // Small delay between tests
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setTestResults(results);

    } catch (error) {
    } finally {
      setIsRunning(false);
    }
  };

  // Clear all caches
  const handleClearCache = () => {
    clusterManager.clearCache();
    setMetrics(prev => ({ ...prev, iconCacheSize: 0, clusterCacheSize: 0 }));
  };

  const getPerformanceColor = (time) => {
    const timeMs = parseFloat(time) || 0;
    return timeMs < 10 ? 'text-green-600' :
           timeMs < 50 ? 'text-yellow-600' : 'text-red-600';
  };

  const getLoadColor = (count) => {
    return count < 100 ? 'text-green-600' :
           count < 300 ? 'text-yellow-600' : 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">🗺️ Map Performance Monitor</h3>
        <div className="space-x-2">
          <button 
            onClick={runPerformanceTest}
            disabled={isRunning}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isRunning ? 'Testing...' : 'Performance Test'}
          </button>
          <button 
            onClick={handleClearCache}
            className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Clear Cache
          </button>
        </div>
      </div>

      {/* Current Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">🚗 Vehicle Load</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Total Vehicles:</span>
              <span className={`font-mono ${getLoadColor(totalVehicles)}`}>
                {totalVehicles}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Visible:</span>
              <span className="font-mono text-gray-700">{visibleVehicles || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Culled:</span>
              <span className="font-mono text-gray-500">
                {metrics.markersCulled || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">🎯 Clustering</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Status:</span>
              <span className={`font-medium ${
                enableClustering ? 'text-green-600' : 'text-red-600'
              }`}>
                {enableClustering ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Clusters:</span>
              <span className="font-mono text-gray-700">
                {metrics.clustersCreated || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Rendered:</span>
              <span className="font-mono text-gray-700">
                {metrics.markersRendered || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <h4 className="font-semibold text-purple-800 mb-2">⚡ Performance</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Render Time:</span>
              <span className={`font-mono ${getPerformanceColor(metrics.renderTime)}`}>
                {metrics.renderTime?.toFixed(1) || '0'}ms
              </span>
            </div>
            <div className="flex justify-between">
              <span>Icon Cache:</span>
              <span className="font-mono text-gray-700">
                {metrics.iconCacheSize || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Last Update:</span>
              <span className="font-mono text-xs text-gray-500">
                {metrics.lastUpdate ? new Date(metrics.lastUpdate).toLocaleTimeString() : 'Never'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">📊 Efficiency</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Compression:</span>
              <span className="font-mono text-yellow-700">
                {totalVehicles && metrics.markersRendered 
                  ? `${((1 - metrics.markersRendered / totalVehicles) * 100).toFixed(0)}%`
                  : '0%'
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span>Memory:</span>
              <span className="font-mono text-yellow-700">
                {((metrics.iconCacheSize || 0) * 1.5).toFixed(1)}KB
              </span>
            </div>
            <div className="flex justify-between">
              <span>Status:</span>
              <span className={`text-xs font-medium ${
                (metrics.renderTime || 0) < 50 ? 'text-green-600' : 'text-red-600'
              }`}>
                {(metrics.renderTime || 0) < 50 ? '✅ Smooth' : '⚠️ Slow'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Test Results */}
      {testResults && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-gray-800 mb-3">🧪 Performance Test Results</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-2">Scenario</th>
                  <th className="text-right p-2">Vehicles</th>
                  <th className="text-right p-2">Clusters</th>
                  <th className="text-right p-2">Cluster Time</th>
                  <th className="text-right p-2">Individual Time</th>
                  <th className="text-right p-2">Improvement</th>
                </tr>
              </thead>
              <tbody>
                {testResults.map((result, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="p-2 font-medium">{result.name}</td>
                    <td className="p-2 text-right font-mono">{result.count}</td>
                    <td className="p-2 text-right font-mono">{result.clustersCreated}</td>
                    <td className={`p-2 text-right font-mono ${getPerformanceColor(result.clusterTime)}`}>
                      {result.clusterTime}ms
                    </td>
                    <td className={`p-2 text-right font-mono ${getPerformanceColor(result.individualTime)}`}>
                      {result.individualTime}ms
                    </td>
                    <td className="p-2 text-right font-mono text-green-600">
                      {result.improvement > 0 ? '+' : ''}{result.improvement}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Performance Recommendations */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-3">💡 Performance Recommendations</h4>
        <div className="space-y-2 text-sm">
          {totalVehicles > 200 && !enableClustering && (
            <div className="flex items-center text-red-700">
              <span className="mr-2">🚨</span>
              <span>High vehicle count detected! Enable clustering for better performance.</span>
            </div>
          )}
          {(metrics.renderTime || 0) > 100 && (
            <div className="flex items-center text-yellow-700">
              <span className="mr-2">⚠️</span>
              <span>Slow rendering detected. Consider reducing zoom level or clearing cache.</span>
            </div>
          )}
          {enableClustering && totalVehicles > 50 && (
            <div className="flex items-center text-green-700">
              <span className="mr-2">✅</span>
              <span>Clustering is optimizing performance for {totalVehicles} vehicles.</span>
            </div>
          )}
          {totalVehicles > 1000 && (
            <div className="flex items-center text-blue-700">
              <span className="mr-2">📊</span>
              <span>Extreme load detected. Monitor memory usage and consider pagination.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapPerformanceMonitor;